import { Document, IFile, APIOutput } from "@amzn/ask-legal-domain";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Box,
  Button,
  Modal,
  ProgressBar,
  SpaceBetween,
  Table,
  Header,
  StatusIndicator,
  TextFilter,
  Pagination
} from "@amzn/awsui-components-react";
import * as React from "react";
import { AppContext } from "../../setup/context";
import { DocumentIcon } from "./DocumentIcon";

const DEFAULT_PAGE_SIZE = 25;

export interface UIDocument extends Document {
  id: string;
  groupName?: string;
}

interface MultipleDocumentDownloadModalProps {
  visible: boolean;
  onDismiss: () => void;
  documents: UIDocument[];
}

export const MultipleDocumentDownloadModal: React.FC<MultipleDocumentDownloadModalProps> = ({
  visible,
  onDismiss,
  documents
}) => {
  const context = React.useContext(AppContext);
  const [downloadStatus, setDownloadStatus] = React.useState<{[key: string]: string}>({});
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [completedCount, setCompletedCount] = React.useState(0);
  const [selectedItems, setSelectedItems] = React.useState<UIDocument[]>([]);

  const uiDocuments = React.useMemo(() => {
    return documents?.filter(Boolean) || [];
  }, [documents]);

  const { items, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    uiDocuments,
    {
      filtering: {
        filteringFunction: (item, filteringText) => {
          if (!filteringText) return true;
          const filterText = filteringText.toLowerCase();
          return (
            item.filename.toLowerCase().includes(filterText) ||
            (item.description && item.description.toLowerCase().includes(filterText)) ||
            (item.groupName && item.groupName.toLowerCase().includes(filterText))
          );
        },
        empty: (
          <Box textAlign="center" color="inherit">
            <b>No documents</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No documents available.
            </Box>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>No matches</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No documents match the filter.
            </Box>
          </Box>
        )
      },
      pagination: { pageSize: DEFAULT_PAGE_SIZE },
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: "filename"
          }
        }
      }
    }
  );

  React.useEffect(() => {
    if (visible) {
      setSelectedItems([]);
      setDownloadStatus({});
      setIsDownloading(false);
      setCompletedCount(0);
    }
  }, [visible]);

  const handleSelectionChange = ({ detail }: { detail: { selectedItems: UIDocument[] } }) => {
    const currentPageIds = items.map(item => item.id);
    const selectedFromOtherPages = selectedItems.filter(item => !currentPageIds.includes(item.id));
    setSelectedItems([...selectedFromOtherPages, ...detail.selectedItems]);
  };

  const getCurrentPageSelectedItems = () => {
    return items.filter(item => selectedItems.some(selected => selected.id === item.id));
  };

  const downloadDocument = async (document: UIDocument) => {
    if (!document?.s3File?.key || !document.filename) {
      setDownloadStatus(prev => ({ ...prev, [document.id]: "error" }));
      setCompletedCount(prev => prev + 1);
      return false;
    }

    try {
      setDownloadStatus(prev => ({ ...prev, [document.id]: "in-progress" }));
      const rawOutput = await context.getFileAPI().getPresignedUrl(
        IFile.PresignedUrlInput.create({
          key: document.s3File.key,
          operation: IFile.PresignedOperation.Get,
          versionId: document.s3File.versionId
        })
      );

      const presignedUrlOutput = APIOutput.fromRaw<IFile.PresignedUrlOutput>(rawOutput.data);
      if (presignedUrlOutput.isErr()) {
        throw new Error(presignedUrlOutput.err.message);
      }

      const response = await fetch(presignedUrlOutput.data.presignedUrl);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Empty file downloaded");
      }

      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadStatus(prev => ({ ...prev, [document.id]: "complete" }));
      setCompletedCount(prev => prev + 1);
      return true;
    } catch (error) {
      setDownloadStatus(prev => ({ ...prev, [document.id]: "error" }));
      setCompletedCount(prev => prev + 1);
      return false;
    }
  };

  const startDownload = async () => {
    if (!selectedItems?.length) return;

    setIsDownloading(true);
    setCompletedCount(0);

    for (const doc of selectedItems.filter(Boolean)) {
      await downloadDocument(doc);
    }

    setIsDownloading(false);
  };

  const progressPercentage = selectedItems.length > 0
    ? Math.round((completedCount / selectedItems.length) * 100)
    : 0;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={<Header>Download Multiple Documents</Header>}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={isDownloading}>Cancel</Button>
            <Button
              variant="primary"
              onClick={startDownload}
              disabled={selectedItems.length === 0 || isDownloading}
            >
              {isDownloading ? "Downloading..." : "Download Selected"}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size="max"
    >
      <SpaceBetween direction="vertical" size="l">
        {isDownloading && (
          <ProgressBar
            value={progressPercentage}
            label="Download Progress"
            description={`${completedCount}/${selectedItems.length} documents`}
          />
        )}
        <Table
          {...collectionProps}
          enableKeyboardNavigation={true}
          items={items}
          selectionType="multi"
          selectedItems={getCurrentPageSelectedItems()}
          onSelectionChange={handleSelectionChange}
          stickyHeader={true}
          stripedRows={true}
          resizableColumns={true}
          columnDefinitions={[
            {
              id: "document",
              header: "Document",
              sortingField: "filename",
              minWidth: 800,
              cell: (item: UIDocument) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <DocumentIcon filename={item.filename} />
                  <div style={{ marginLeft: "10px" }}>
                    <div>{item.filename}</div>
                    <div style={{ color: "#5f6b7a", fontSize: "12px" }}>{item.description}</div>
                  </div>
                </div>
              )
            },
            {
              id: "group",
              header: "Group",
              sortingField: "groupName",
              minWidth: 300,
              cell: (item: UIDocument) => item.groupName || "-"
            },
            {
              id: "status",
              header: "Status",
              sortingField: "status",
              minWidth: 200,
              cell: (item: UIDocument) => {
                const status = downloadStatus[item.id];
                const isSelected = selectedItems.some(doc => doc.id === item.id);
                if (status === "complete") {
                  return <StatusIndicator type="success">Complete</StatusIndicator>;
                } else if (status === "error") {
                  return <StatusIndicator type="error">Failed</StatusIndicator>;
                } else if (status === "in-progress") {
                  return <StatusIndicator type="in-progress">Downloading</StatusIndicator>;
                } else if (isSelected && isDownloading) {
                  return <StatusIndicator type="pending">Queued</StatusIndicator>;
                }
                return "-";
              }
            }
          ]}
          header={(
            <Header
              counter={`(${selectedItems.length}/${uiDocuments.length})`}
              actions={(
                <Button
                  disabled={selectedItems.length === 0}
                  onClick={() => setSelectedItems([])}
                >
                  Clear Selection
                </Button>
              )}
            >
              Select Documents
            </Header>
          )}
          filter={
            <TextFilter
              {...filterProps}
              filteringAriaLabel="Filter documents"
              filteringPlaceholder="Find documents"
              filteringClearAriaLabel="Clear"
              countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? "document" : "documents"}`}
            />
          }
          pagination={<Pagination {...paginationProps} />}
        />
      </SpaceBetween>
    </Modal>
  );
};